import type {
  ReportDimension,
  ReportMeasure,
  ReportRow,
} from '@/features/reports/api/use-sales-report-query'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'
import { formatRowLabel } from '@/features/reports/lib/dimension-labels'
import { computeDelta, formatDelta } from '@/features/reports/lib/delta'

type Translate = (key: string, options?: Record<string, unknown>) => string

export type ColumnKind = 'label' | 'measure' | 'share' | 'secondary' | 'delta'

export interface BreakdownColumn {
  key: string
  label: string
  align: 'left' | 'right'
  kind: ColumnKind
  /** Formatted for the screen. */
  format: (row: ReportRow) => string
  /** Unformatted for CSV — cents and counts, so a spreadsheet can compute with them. */
  raw: (row: ReportRow) => string | number
}

/**
 * The table's columns, built from `t` rather than hardcoded German
 * (CHG-2026-09-manager-reporting).
 *
 * The existing sales report keeps its column labels as German string literals in a config file,
 * which bypasses i18n entirely. This is the pattern the inventory and delivery columns use instead.
 *
 * **The measure switch moves three things together**, which is the whole reason it is a switch and
 * not a column: the leading figure, the bar the share is drawn from, and the sort order. The other
 * measure stays as a secondary column so nobody has to toggle back to read it — by revenue food
 * leads, by piece count drinks do, and both numbers are on screen either way.
 *
 * The sort itself is the server's: `ORDER BY <measure> DESC, key ASC`, so paging stays a total
 * order even when two rows tie.
 */
export function buildBreakdownColumns(
  t: Translate,
  dimension: ReportDimension,
  measure: ReportMeasure,
  /** Set when the hour rows span more than one day, so each one names its date. */
  options?: { withDate?: boolean },
): Array<BreakdownColumn> {
  const revenue: BreakdownColumn = {
    key: 'revenue',
    label: t('measure.revenue'),
    align: 'right',
    kind: measure === 'revenue' ? 'measure' : 'secondary',
    format: (row) => formatCurrency(row.revenueCents),
    raw: (row) => row.revenueCents,
  }

  const units: BreakdownColumn = {
    key: 'units',
    label: t('measure.units'),
    align: 'right',
    kind: measure === 'units' ? 'measure' : 'secondary',
    format: (row) => formatNumber(row.units),
    raw: (row) => row.units,
  }

  const leading = measure === 'units' ? units : revenue
  const trailing = measure === 'units' ? revenue : units

  return [
    {
      key: 'label',
      label: t(`dimension.columnLabel.${dimension}`, {
        defaultValue: t('dimension.label'),
      }),
      align: 'left',
      kind: 'label',
      format: (row) => formatRowLabel(t, dimension, row, options),
      raw: (row) => formatRowLabel(t, dimension, row, options),
    },
    leading,
    {
      key: 'share',
      label: t('columns.share'),
      align: 'left',
      kind: 'share',
      // The share is computed against the pinned total by the table, not by a column: it needs the
      // total, and duplicating it per row is how two numbers on the same screen start to disagree.
      format: () => '',
      raw: () => '',
    },
    trailing,
    {
      key: 'bons',
      label: t('columns.bons'),
      align: 'right',
      kind: 'secondary',
      format: (row) => formatNumber(row.bons),
      raw: (row) => row.bons,
    },
    {
      key: 'averageBon',
      label: t('columns.averageBon'),
      align: 'right',
      kind: 'secondary',
      format: (row) =>
        row.bons > 0
          ? formatCurrency(Math.round(row.revenueCents / row.bons))
          : '—',
      raw: (row) =>
        row.bons > 0 ? Math.round(row.revenueCents / row.bons) : '',
    },
    {
      key: 'delta',
      label: t('columns.delta'),
      align: 'right',
      kind: 'delta',
      format: (row) =>
        formatDelta(
          computeDelta(
            measure === 'units' ? row.units : row.revenueCents,
            measure === 'units' ? row.previousUnits : row.previousRevenueCents,
          ),
        ) ?? '—',
      raw: (row) =>
        computeDelta(
          measure === 'units' ? row.units : row.revenueCents,
          measure === 'units' ? row.previousUnits : row.previousRevenueCents,
        ).ratio ?? '',
    },
  ]
}

/** The figure the share bar and the sort are based on — it follows the measure, never the column. */
export function measureValueOf(row: ReportRow, measure: ReportMeasure): number {
  return measure === 'units' ? row.units : row.revenueCents
}

/** Which dimensions have a level below them, so a row click means something. */
export function drillTargetOf(
  dimension: ReportDimension,
): ReportDimension | null {
  switch (dimension) {
    case 'time_week':
      return 'time_day'
    case 'time_day':
      return 'time_hour'
    case 'category_main':
      return 'category_sub'
    case 'category_sub':
      return 'article'
    default:
      return null
  }
}
